import {AfterViewInit, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {
    DetectMethodChanges,
    DxValueChanged,
    isVoid,
} from "../../../utils";
import {ExpirationDescriptor, HgHedgeMatrixComponent} from "../hg-hedge-matrix.component";

const SINGLE_MODE_WIDTH = 43;

@Component({
    selector: 'ets-hg-hedge-matrix-hedges-selector',
    templateUrl: 'hg-hedge-matrix-hedges-selector.component.html',
    styleUrls: ['hg-hedge-matrix-hedges-selector.component.scss']
})

export class HgHedgeMatrixHedgesSelectorComponent implements OnInit, AfterViewInit {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef
    ) {
    }

    ngAfterViewInit(): void {
    }

    cmp: HgHedgeMatrixComponent;

    visible: boolean;

    shortView = false;

    height = 550;

    width = SINGLE_MODE_WIDTH + 'vw';

    shortViewUnderlying = 'spx';
    shortViewColumn = 'pricing';

    get shortViewUnderlyingOptions(): { value: string, label: string }[] {
        const options: { value: string, label: string }[] = [
            { value: 'spx', label: 'SPX' }
        ];
        for (const section of this.nonBaseUnderlyingSections) {
            options.push({ value: section.underlying, label: section.displayName });
        }
        return options;
    }

    get allHedgesSelected(): boolean {
        return this.cmp.getColumnSelectorTextHedges() === 'All Hedges Selected';
    }

    get allExpirationsSelected(): boolean {
        return this.cmp.getColumnSelectorTextExpirations() === 'All Expirations Selected';
    }

    get allPnlExpirationsSelected() {
        return this.cmp.pnlExpirationsList.every(x => {
            return this.isPnlExpirationSelected(x, false);
        });
    }

    get callHedgeList(): any[] {
        if (isVoid(this.cmp)) {
            return [];
        }
        return this.cmp.hedgeList.filter(x => x.side === 'Call');
    }

    get putHedgeList(): any[] {
        if (isVoid(this.cmp)) {
            return [];
        }
        return this.cmp.hedgeList.filter(x => x.side === 'Put');
    }

    // ── Row identity for *ngFor ──
    // Every list in this dialog is re-read on each change-detection pass, and several of them
    // build fresh row objects per read. Without a trackBy Angular treats a fresh object as a new
    // row: the checkbox under the pointer is destroyed and re-created between mouse-down and
    // mouse-up, and the browser delivers the click to the container instead — a lost toggle.
    // The keys below make the row identity stable across passes.

    trackByExpiration = (_: number, item: { expiration: string, side?: string }) =>
        `${item.expiration}|${item.side || ''}`;

    trackByGroup = (_: number, group: { expiration: { expiration: string, side?: string } }) =>
        `${group.expiration.expiration}|${group.expiration.side || ''}`;

    trackByHedge = (_: number, hedge: { id: string }) => hedge.id;

    trackBySection = (_: number, section: { underlying: string }) => section.underlying;

    // Derived-list memo: one derived array per (source array, tag), rebuilt only when the source
    // array reference changes (i.e. when the matrix refills its reference lists).
    private readonly _derivedLists = new WeakMap<object, Map<string, any[]>>();

    private derived<T>(source: object, tag: string, build: () => T[]): T[] {
        let perSource = this._derivedLists.get(source);
        if (!perSource) {
            perSource = new Map<string, any[]>();
            this._derivedLists.set(source, perSource);
        }
        let list = perSource.get(tag);
        if (!list) {
            list = build();
            perSource.set(tag, list);
        }
        return list as T[];
    }

    get callExpirationList(): any[] {
        if (isVoid(this.cmp)) {
            return [];
        }
        const source = this.cmp.uniqueExpirationList;
        return this.derived(source, 'call', () => source.map(x => {
            return {
                expiration: x.expiration,
                expirationFull: x.expirationFull,
                dayOfWeek: x.dayOfWeek,
                dayOfWeekShort: x.dayOfWeekShort,
                dte: x.dte,
                side: 'Call'
            };
        }));
    }

    get putExpirationList(): any[] {
        if (isVoid(this.cmp)) {
            return [];
        }
        const source = this.cmp.uniqueExpirationList;
        return this.derived(source, 'put', () => source.map(x => {
            return {
                expiration: x.expiration,
                expirationFull: x.expirationFull,
                dayOfWeek: x.dayOfWeek,
                dayOfWeekShort: x.dayOfWeekShort,
                dte: x.dte,
                side: 'Put'
            };
        }));
    }

    get callPnlExpirationList(): any[] {
        if (isVoid(this.cmp)) {
            return [];
        }
        return this.cmp.pnlExpirationsList.filter(x => x.side === 'Call');
    }

    get putPnlExpirationList(): any[] {
        if (isVoid(this.cmp)) {
            return [];
        }
        return this.cmp.pnlExpirationsList.filter(x => x.side === 'Put');
    }

    get expirationList(): Partial<ExpirationDescriptor>[] {
        if (isVoid(this.cmp)) {
            return [];
        }
        return this.cmp.uniqueExpirationList;
    }

    get uniqueExpirations(): any[] {
        return this.cmp.uniqueExpirationList;
    }

    get callHedgesByExpiration(): any[] {
        return this.cmp.callHedgesByExpiration;
    }

    get putHedgesByExpiration(): any[] {
        return this.cmp.putHedgesByExpiration;
    }

    /** Returns non-base underlying sections (e.g., ES contracts) that have hedges */
    get nonBaseUnderlyingSections(): any[] {
        if (isVoid(this.cmp)) {
            return [];
        }
        return this.cmp.nonBaseUnderlyingSections || [];
    }

    get hasNonBaseUnderlyings(): boolean {
        return this.cmp?.esHedgingEnabled && this.nonBaseUnderlyingSections.length > 0;
    }

    // ── ES section-scoped methods ──

    /** Returns ES sections to display: all in full view, selected one in compact view */
    get visibleEsSections(): any[] {
        if (!this.hasNonBaseUnderlyings) { return []; }
        if (this.shortView) {
            if (this.shortViewUnderlying === 'spx') { return []; }
            const section = this.nonBaseUnderlyingSections
                .find(s => s.underlying === this.shortViewUnderlying);
            return section ? [section] : [];
        }
        return this.nonBaseUnderlyingSections;
    }

    // ── Section data getters ──

    getSectionCallHedgeList(section: any): any[] {
        return section.hedgeList.filter(x => x.side === 'Call');
    }

    getSectionPutHedgeList(section: any): any[] {
        return section.hedgeList.filter(x => x.side === 'Put');
    }

    getSectionUniqueExpirations(section: any): any[] {
        return this.derived(section, 'unique', () => {
            const seen = new Map<string, any>();
            for (const exp of section.expirationList) {
                if (!seen.has(exp.expiration)) {
                    seen.set(exp.expiration, {
                        expiration: exp.expiration,
                        expirationFull: exp.expirationFriendly,
                        expirationFriendly: exp.expirationFriendly,
                        dayOfWeek: exp.dayOfWeek,
                        dayOfWeekShort: exp.dayOfWeekShort,
                    });
                }
            }
            return Array.from(seen.values());
        });
    }

    getSectionCallExpirationList(section: any): any[] {
        return this.derived(section, 'call', () =>
            this.getSectionUniqueExpirations(section).map(x => ({...x, side: 'Call'})));
    }

    getSectionPutExpirationList(section: any): any[] {
        return this.derived(section, 'put', () =>
            this.getSectionUniqueExpirations(section).map(x => ({...x, side: 'Put'})));
    }

    getSectionCallPnlExpirationList(section: any): any[] {
        return section.pnlExpirationsList.filter(x => x.side === 'Call');
    }

    getSectionPutPnlExpirationList(section: any): any[] {
        return section.pnlExpirationsList.filter(x => x.side === 'Put');
    }

    getSectionExpirationList(section: any): Partial<ExpirationDescriptor>[] {
        return this.getSectionUniqueExpirations(section);
    }

    getSectionHedgesByExpiration(section: any, side: 'Call' | 'Put'): any[] {
        const hedgeIds = new Set(section.hedgeList.map(h => h.id));
        const uniqueExps = this.getSectionUniqueExpirations(section);
        return uniqueExps.map(exp => {
            const matchingHedges = this.cmp.hedgeMatrixDataService.getHedges()
                .filter(h => hedgeIds.has(h.id) && h.type === side)
                .filter(h => {
                    const hExps = this.cmp.hedgeMatrixDataService.getHedgeExpirations(h.id);
                    return hExps.includes(exp.expiration);
                })
                .map(h => section.hedgeList.find(y => y.id === h.id))
                .filter(h => !!h);
            return {expiration: {...exp, side}, hedges: matchingHedges};
        }).filter(g => g.hedges.length > 0);
    }

    // ── Section select-all state checks ──

    isSectionAllHedgesSelected(section: any): boolean {
        return section.hedgeList.length > 0 &&
            section.hedgeList.every(h => this.isHedgeSelected(h));
    }

    isSectionAllExpirationsSelected(section: any): boolean {
        const allExps = [
            ...this.getSectionCallExpirationList(section),
            ...this.getSectionPutExpirationList(section)
        ];
        return allExps.length > 0 &&
            allExps.every(x => this.cmp.isEsExpirationSelected(x, section.underlying));
    }

    isSectionAllPnlExpirationsSelected(section: any): boolean {
        return section.pnlExpirationsList.length > 0 &&
            section.pnlExpirationsList.every(x =>
                this.cmp.isEsPnlExpirationSelected(x, false, section.underlying));
    }

    // ── Section toggle-all methods ──

    toggleSectionAllHedges($event: DxValueChanged<boolean>, section: any) {
        if (isVoid($event.event)) { return; }
        setTimeout(() => {
            section.hedgeList.forEach(hedge => {
                this.onHedgeSelectionChanged({value: $event.value, event: 'ets'}, hedge);
            });
        });
    }

    toggleSectionAllExpirations($event: DxValueChanged<boolean>, section: any) {
        if (isVoid($event.event)) { return; }
        setTimeout(() => {
            const allExps = [
                ...this.getSectionCallExpirationList(section),
                ...this.getSectionPutExpirationList(section)
            ];
            allExps.forEach(exp => {
                this.cmp.onEsExpirationSelectionChanged(exp, section.underlying, $event.value);
            });
        });
    }

    toggleSectionAllPnlExpirations($event: DxValueChanged<boolean>, section: any) {
        if (isVoid($event.event)) { return; }
        setTimeout(() => {
            section.pnlExpirationsList.forEach(exp => {
                this.cmp.onEsPnlExpirationSelectionChanged(exp, $event.value, false, section.underlying);
            });
        });
    }

    // ── Section individual toggle methods ──

    onSectionExpirationSelectionChanged($event: DxValueChanged<boolean>, expiration: any, section: any) {
        if (!$event.event) { return; }
        this.cmp.onEsExpirationSelectionChanged(expiration, section.underlying);
    }

    isSectionExpirationSelected(expiration: any, section: any): boolean {
        return this.cmp.isEsExpirationSelected(expiration, section.underlying);
    }

    onSectionPnlExpirationSelectionChanged($event: any, expiration: any, allInclusive: boolean, section: any) {
        if (!$event.event) { return; }
        this.cmp.onEsPnlExpirationSelectionChanged(expiration, $event.value, allInclusive, section.underlying);
    }

    isSectionPnlExpirationSelected(expiration: any, allInclusive: boolean, section: any): boolean {
        return this.cmp.isEsPnlExpirationSelected(expiration, allInclusive, section.underlying);
    }

    onSectionGrandTotalExpirationSelectionChanged($event: any, item: any, section: any) {
        if (isVoid($event.event)) { return; }
        this.cmp.onEsGrandTotalExpirationSelectionChanged(item, section.underlying);
    }

    isSectionGrandTotalExpirationSelected(item: any, section: any): boolean {
        return this.cmp.isEsGrandTotalExpirationSelected(item, section.underlying);
    }

    onSectionNormalizedExpirationPnlToggled($event: DxValueChanged<boolean>, item: Partial<ExpirationDescriptor>) {
        this.cmp.onNormalizedExpirationPnlToggled($event.value, item);
    }

    isSectionNormalizedExpirationPnlEnabled(item: Partial<ExpirationDescriptor>): boolean {
        return this.cmp.isNormalizedExpirationPnlEnabled(item);
    }

    // ── Section day toggle methods ──

    isSectionDayToggleSelected(expiration: any, section: any) {
        const selections = section.hedgeList
            .filter(x => {
                const hedgeExpirations = this.cmp.hedgeMatrixDataService.getHedgeExpirations(x.id);
                return hedgeExpirations.includes(expiration.expiration);
            })
            .map(x => this.isHedgeSelected(x));
        if (selections.length === 0) { return false; }
        const allSelected = selections.every(x => x);
        if (allSelected) { return true; }
        const noneSelected = selections.every(x => !x);
        if (noneSelected) { return false; }
        return undefined;
    }

    onSectionDayToggleSelectionChanged(expiration: any, $event: DxValueChanged<boolean>, section: any) {
        if (isVoid($event.event)) { return; }
        const filteredHedges = section.hedgeList.filter(x => {
            const hedgeExpirations = this.cmp.hedgeMatrixDataService.getHedgeExpirations(x.id);
            return hedgeExpirations.includes(expiration.expiration);
        });
        setTimeout(() => {
            filteredHedges.forEach(hedge => {
                this.onHedgeSelectionChanged({value: $event.value, event: 'ets'}, hedge);
            });
        });
    }

    @DetectMethodChanges()
    onEsHedgingModeChanged($event: DxValueChanged<boolean>) {
        if (this.cmp) {
            this.cmp.onEsHedgingModeChanged($event);
        }
    }

    ngOnInit() {

    }

    @DetectMethodChanges()
    show(p: HgHedgeMatrixComponent) {
        this.shortViewUnderlying = 'spx';
        this.shortViewColumn = 'pricing';

        this.cmp = p;

        if (!this.shortView) {
            const esCount = this.nonBaseUnderlyingSections.length;
            const isSingleMode = esCount === 0 || !this.cmp.esHedgingEnabled;
            this.width = (!isSingleMode ? 33 + (esCount * 33) + 1 :SINGLE_MODE_WIDTH) + 'vw';
        }
        this.visible = true;
    }

    @DetectMethodChanges()
    onHidden() {
        this.visible = false;
    }

    onHedgeSelectionChanged($event: DxValueChanged<boolean>, hedge) {
        if (!$event.event) {
            return;
        }
        this.cmp.onHedgeSelectionChanged(hedge, $event?.value);
    }

    onExpirationSelectionChanged($event: DxValueChanged<boolean>, expiration) {
        if (!$event.event) {
            return;
        }
        this.cmp.onExpirationSelectionChanged(expiration);
    }

    @DetectMethodChanges()
    toggleSelectAllHedges($event: DxValueChanged<boolean>) {
        if (isVoid($event.event)) {
            return;
        }
        setTimeout(() => {
            this.cmp.toggleSelectAllHedges($event);
        });
    }

    toggleSelectAllExpirations(event: DxValueChanged<boolean>) {
        if (isVoid(event.event)) {
            return;
        }
        setTimeout(() => {
            this.cmp.toggleSelectAllExpirations(event);
        });
    }

    isHedgeSelected(hedge: any): boolean {
        return this.cmp.isHedgeSelected(hedge);
    }

    isExpirationSelected(hedge: any): boolean {
        return this.cmp.isExpirationSelected(hedge);
    }

    onPnlExpirationSelectionChanged($event: any, expiration: any, allInclusive: boolean) {
        if (!$event.event) {
            return;
        }
        this.cmp.onPnlExpirationSelectionChanged(expiration, null, allInclusive);
    }

    isPnlExpirationSelected(expiration: any, allInclusive: boolean) {
        return this.cmp.isPnlExpirationSelected(expiration, allInclusive);
    }

    toggleSelectAllPnlExpirations($event: any) {
        if (isVoid($event.event)) {
            return;
        }
        setTimeout(() => {
            this.cmp.toggleSelectAllPnlExpirations($event);
        });
    }

    onGrandTotalExpirationSelectionChanged($event: any, item: any) {
        if (isVoid($event.event)) {
            return;
        }
        this.cmp.onGrandTotalExpirationSelectionChanged(item);
    }

    isGrandTotalExpirationSelected(item: any) {
        return this.cmp.isGrandTotalExpirationSelected(item);
    }

    onResizeEnd(args: any) {
        // this.shortView = args.width <= 360;
    }

    @DetectMethodChanges()
    onResize(args: any) {
        const width = args.actionValue[0].width;
        this.shortView = width <= 360;
    }

    @DetectMethodChanges()
    shrinkWindow() {
        this.shortView = true;
        this.shortViewUnderlying = 'spx';
        this.shortViewColumn = 'pricing';
        this.width = '25vw';
    }

    @DetectMethodChanges()
    expandWindow() {
        this.shortView = false;
        const esCount = this.nonBaseUnderlyingSections.length;
        const isSingleMode = esCount === 0 || !this.cmp.esHedgingEnabled;
        this.width = (!isSingleMode ? 33 + (esCount * 33) + 1 : SINGLE_MODE_WIDTH) + 'vw';
    }

    /** Flips between Pricings and Total P&L within the current underlying */
    @DetectMethodChanges()
    switchColumn() {
        this.shortViewColumn = this.shortViewColumn === 'pricing' ? 'pnl' : 'pricing';
    }

    /** Flips between SPX and ES */
    @DetectMethodChanges()
    switchUnderlying() {
        this.shortViewUnderlying = this.shortViewUnderlying === 'spx' ? 'es' : 'spx';
    }

    isPnlSideVisible() {
        return !this.shortView || (this.shortViewColumn === 'pnl' && this.shortView)
    }

    isPricingSideVisible() {
        return !this.shortView || (this.shortViewColumn === 'pricing' && this.shortView)
    }

    /** SPX unit visible: always in full mode, or when shortViewUnderlying is 'spx' in shrink */
    isSpxUnitVisible() {
        return !this.shortView || this.shortViewUnderlying === 'spx';
    }

    /** ES unit visible: only when ES positions exist. Always in full mode, or shortViewUnderlying is an ES underlying */
    isEsUnitVisible() {
        if (!this.hasNonBaseUnderlyings) {
            return false;
        }
        return !this.shortView || this.shortViewUnderlying !== 'spx';
    }

    /** In compact mode: is the Pricings side visible for the current underlying? */
    isCompactPricingVisible() {
        return !this.shortView || this.shortViewColumn === 'pricing';
    }

    /** In compact mode: is the Total P&L side visible for the current underlying? */
    isCompactPnlVisible() {
        return !this.shortView || this.shortViewColumn === 'pnl';
    }

    @DetectMethodChanges()
    closeWindow() {
        this.visible = false;
    }

    onNormalizedExpirationPnlToggled($event: DxValueChanged<boolean>, item: Partial<ExpirationDescriptor>) {
        this.cmp.onNormalizedExpirationPnlToggled($event.value, item);
    }

    isNormalizedExpirationPnlEnabled(item: Partial<ExpirationDescriptor>): boolean {
        return this.cmp.isNormalizedExpirationPnlEnabled(item);
    }

    onToggleModifications(hedge: any, onOff: boolean) {
        this.cmp.toggleHedgeModifications(hedge, onOff);
    }

    isDayToggleSelected(expiration: any) {
        const selections = this.cmp.hedgeList
            .filter(x => {
                const hedgeExpirations = this.cmp.hedgeMatrixDataService.getHedgeExpirations(x.id);
                return hedgeExpirations.includes(expiration.expiration);
            })
            .map(x => this.isHedgeSelected(x));

        const allSelected = selections.every(x => x);

        if (allSelected) return true;

        const noneSelected = selections.every(x => !x);

        if (noneSelected) return false;

        return undefined;
    }

    getHedgeGroupSelectionState(group: {hedges: any[], expiration: any}): boolean | undefined {
        const selections = group.hedges.map(x => this.isHedgeSelected(x));

        const allSelected = selections.every(x => x);

        if (allSelected) {
            return true;
        }
        const noneSelected = selections.every(x => !x);

        if (noneSelected) {
            return false;
        }

        return undefined;
    }

    onHedgeGroupSelectionChanged(group: { hedges: any[], expiration: any }, $event: DxValueChanged<boolean>) {
        if (isVoid($event.event)) {
            return;
        }

        setTimeout(() => {
            if ($event.value) {
                group.hedges.forEach(hedge => {
                    this.onHedgeSelectionChanged({value: true, event: 'ets'},hedge)
                });
            } else {
                group.hedges.forEach(hedge => {
                    this.onHedgeSelectionChanged({value: false, event: 'ets'},hedge)
                });
            }
        });
    }

    onDayToggleSelectionChanged(expiration: any, $event: DxValueChanged<boolean>) {
        if (isVoid($event.event)) {
            return;
        }

        const filteredHedges = this.cmp.hedgeList.filter(x => {
            const hedgeExpirations = this.cmp.hedgeMatrixDataService.getHedgeExpirations(x.id);
            return hedgeExpirations.includes(expiration.expiration);
        });

        setTimeout(() => {
            filteredHedges.forEach(hedge => {
                this.onHedgeSelectionChanged({value: $event.value, event: 'ets'},hedge)
            });
        });
    }
}